import React from 'react';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  ActionOutboxEntityType,
  Feature,
  ListingDetails,
  PurchaseOrderDetails,
  SaleDetails,
} from 'src/WebApiController';

import {
  ActivePosEntityProvider as ActivePosEntityProviderV1,
  IActivePosEntityContext,
  IPosEntity,
  useActivePosEntityContext as useActivePosEntityContextV1,
} from './ActivePosEntityContext';
import {
  ActivePosEntityContextV2Provider,
  GetListingEntityType,
  GetPurchaseEntityType,
  GetSaleEntityType,
  IMultiEntityContext,
  useActivePosEntityContext as useActivePosEntityContextV2,
} from './MultiEntityContextProvider';

/**
 * Feature-flagged ActivePosEntityProvider that switches between V1 and V2 implementations.
 *
 * V1: Original implementation (current production)
 * V2: Multi-entity implementation cross-modal navigation
 *
 */
export const ActivePosEntityProvider = <TDataItem extends IPosEntity>({
  entityType,
  getActivePosEntity,
  getSaleEntity,
  getListingEntity,
  getPurchaseEntity,
  onSetActiveItemCompleteCallback,
  doNotLogPageLoad,
  children,
}: {
  entityType: ActionOutboxEntityType;
  getActivePosEntity: (
    posEntityId: number,
    curEntity?: TDataItem | null,
    dataToGet?: string[] | null
  ) => Promise<
    Pick<
      IActivePosEntityContext<TDataItem>,
      'posEntityId' | 'event' | 'posEntity' | 'posEntityDisplayId'
    >
  >;
  getSaleEntity?: GetSaleEntityType;
  getListingEntity?: GetListingEntityType;
  getPurchaseEntity?: GetPurchaseEntityType;
  onSetActiveItemCompleteCallback?: (
    item: TDataItem,
    forceRefresh?: boolean
  ) => void;
  doNotLogPageLoad?: boolean;
  children: React.ReactNode;
}) => {
  const hasStandaloneEntityDetailModalsFeature = useUserHasFeature(
    Feature.StandaloneEntityDetailModals
  );

  if (hasStandaloneEntityDetailModalsFeature) {
    // V2: Multi-entity implementation
    return (
      <ActivePosEntityContextV2Provider<TDataItem>
        entityType={entityType}
        getActivePosEntity={getActivePosEntity}
        getSaleEntity={getSaleEntity}
        getListingEntity={getListingEntity}
        getPurchaseEntity={getPurchaseEntity}
        onSetActiveItemCompleteCallback={onSetActiveItemCompleteCallback}
        doNotLogPageLoad={doNotLogPageLoad}
      >
        {children}
      </ActivePosEntityContextV2Provider>
    );
  }

  // V1: Original implementation (default) - now passes all props
  return (
    <ActivePosEntityProviderV1<TDataItem>
      entityType={entityType}
      getActivePosEntity={getActivePosEntity}
      getSaleEntity={getSaleEntity}
      getListingEntity={getListingEntity}
      getPurchaseEntity={getPurchaseEntity}
      onSetActiveItemCompleteCallback={onSetActiveItemCompleteCallback}
      doNotLogPageLoad={doNotLogPageLoad}
    >
      {children}
    </ActivePosEntityProviderV1>
  );
};

// Conditional type for proper entity type mapping
type EntityByType<T extends ActionOutboxEntityType> =
  T extends ActionOutboxEntityType.Sale
    ? SaleDetails | null
    : T extends ActionOutboxEntityType.Listing
      ? ListingDetails | null
      : T extends ActionOutboxEntityType.Purchase
        ? PurchaseOrderDetails | null
        : null;

// Helper function to get entity by type from multi-context
const getEntityByType = <T extends ActionOutboxEntityType>(
  multiContext: IMultiEntityContext<IPosEntity>,
  entityType: T
): EntityByType<T> => {
  switch (entityType) {
    case ActionOutboxEntityType.Sale:
      return multiContext.getSaleEntity?.() as EntityByType<T>;
    case ActionOutboxEntityType.Listing:
      return multiContext.getListingEntity?.() as EntityByType<T>;
    case ActionOutboxEntityType.Purchase:
      return multiContext.getPurchaseEntity?.() as EntityByType<T>;
    default:
      return null as EntityByType<T>;
  }
};

// Feature-flagged hook that switches between V1 and V2 implementations
export const useActivePosEntityContext = <
  TDataItem extends IPosEntity = IPosEntity,
>(
  entityType?: ActionOutboxEntityType
) => {
  const hasStandaloneEntityDetailModalsFeature = useUserHasFeature(
    Feature.StandaloneEntityDetailModals
  );

  // Always call both hooks to satisfy React's rules of hooks
  const v1Context = useActivePosEntityContextV1<TDataItem>();
  const v2Context = useActivePosEntityContextV2<TDataItem>();

  // Return the appropriate context based on feature flag
  if (hasStandaloneEntityDetailModalsFeature && entityType) {
    // V2: Return multi-context, but override posEntity for specific type
    return {
      ...v2Context,
      posEntity: getEntityByType(v2Context, entityType) ?? null,
    } as IMultiEntityContext<TDataItem> & {
      posEntity: EntityByType<typeof entityType> | null;
    };
  } else if (hasStandaloneEntityDetailModalsFeature) {
    // V2: Return multi-context as-is
    return v2Context;
  }

  // V1: Return V1 context unchanged
  return v1Context;
};

// Export all the other utilities for backwards compatibility
export {
  createActivePosEntityContext,
  type IActivePosEntityContext,
  type IPosEntity,
} from './ActivePosEntityContext';
export { type IMultiEntityContext } from './MultiEntityContextProvider';
